import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { Sparkles, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useContent, CONCIERGE_DEFAULTS } from "@/lib/content";

const BODY_STYLES = ["SUV", "Sedan", "Truck", "EV", "Coupe", "Minivan", "Hatchback"];
const FUEL = ["Gas", "Hybrid", "Plug-in", "Electric", "No preference"];
const TIMELINE = ["ASAP", "2 weeks", "30 days", "Just exploring"];
const CONTACT = ["Text", "Email", "Call"];

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${active ? "border-rose-600 bg-rose-600 text-white" : "border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 hover:border-slate-400"}`}>
    {children}
  </button>
);

export default function Concierge() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [busy, setBusy] = useState(false);
  const _b = state?.bodies || [];
  const [f, setF] = useState({
    body_style: state?.body_style || (_b.length === 1 ? _b[0] : ""), brands: state?.brands || "", fuel_type: "", budget: "", timeline: "", trade_in: false,
    must_haves: _b.length > 1 ? `Body types: ${_b.join(", ")}` : "", colors: "", zip_code: "", buyer_phone: "", contact_method: "Text", notes: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const c = useContent("concierge", CONCIERGE_DEFAULTS);

  const submit = async () => {
    if (!f.body_style && !f.brands) { toast.error("Tell us at least a body style or a brand you like"); return; }
    if (!f.buyer_phone && !f.zip_code) { toast.error("Add a ZIP or phone so a broker can reach you"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/concierge", f);
      toast.success("Sent! A California broker will start sourcing your car.");
      navigate(`/requests/${data.id}`);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Please sign in to send your request"); navigate("/login"); }
      else toast.error("Could not submit — please try again");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        <div className="ab-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.15em] text-rose-600">
            <Sparkles className="h-3.5 w-3.5" /> Concierge · No obligation
          </span>
          <h1 className="mt-4 font-display text-3xl font-black tracking-tighter text-slate-950 dark:text-slate-50 sm:text-4xl">{c.concierge_heading}</h1>
          <p className="mt-2 text-slate-600 dark:text-slate-300">{c.concierge_subtext}</p>
        </div>

        <div className="mt-8 space-y-5">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6" data-testid="concierge-section-type">
            <h2 className="font-display text-lg font-bold text-slate-900 dark:text-slate-100">What are you after?</h2>
            <Label className="mt-4 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Body style</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {BODY_STYLES.map((b) => <Chip key={b} active={f.body_style === b} onClick={() => set("body_style", f.body_style === b ? "" : b)} testId={`chip-body-${b.toLowerCase()}`}>{b}</Chip>)}
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Brands you like</Label>
                <Input data-testid="concierge-brands" value={f.brands} onChange={(e) => set("brands", e.target.value)} placeholder="e.g. Toyota, Audi" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Fuel</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {FUEL.map((x) => <Chip key={x} active={f.fuel_type === x} onClick={() => set("fuel_type", f.fuel_type === x ? "" : x)} testId={`chip-fuel-${x.toLowerCase().replace(/\s/g, "-")}`}>{x}</Chip>)}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6" data-testid="concierge-section-budget">
            <h2 className="font-display text-lg font-bold text-slate-900 dark:text-slate-100">Budget & timing</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Target budget or monthly payment</Label>
                <Input data-testid="concierge-budget" value={f.budget} onChange={(e) => set("budget", e.target.value)} placeholder="$45k, or $650/mo" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">When do you need it?</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {TIMELINE.map((x) => <Chip key={x} active={f.timeline === x} onClick={() => set("timeline", f.timeline === x ? "" : x)} testId={`chip-timeline-${x.toLowerCase().replace(/\s/g, "-")}`}>{x}</Chip>)}
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between rounded-md border border-slate-200 dark:border-slate-800 p-3">
              <div><p className="text-sm font-semibold text-slate-800 dark:text-slate-200">I have a trade-in</p><p className="text-xs text-slate-400 dark:text-slate-500">We'll value it during sourcing</p></div>
              <Switch checked={f.trade_in} onCheckedChange={(v) => set("trade_in", v)} data-testid="concierge-tradein" />
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6" data-testid="concierge-section-details">
            <h2 className="font-display text-lg font-bold text-slate-900 dark:text-slate-100">Must-haves & where you are</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Must-have features</Label>
                <Input data-testid="concierge-musthaves" value={f.must_haves} onChange={(e) => set("must_haves", e.target.value)} placeholder="AWD, sunroof, towing" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Color preferences</Label>
                <Input data-testid="concierge-colors" value={f.colors} onChange={(e) => set("colors", e.target.value)} placeholder="White exterior, black interior" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">ZIP code</Label>
                <Input data-testid="concierge-zip" value={f.zip_code} onChange={(e) => set("zip_code", e.target.value)} placeholder="90001" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Mobile number</Label>
                <Input data-testid="concierge-phone" value={f.buyer_phone} onChange={(e) => set("buyer_phone", e.target.value)} placeholder="+1 415 555 1234" className="mt-1.5" />
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Best way to reach you</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {CONTACT.map((x) => <Chip key={x} active={f.contact_method === x} onClick={() => set("contact_method", x)} testId={`chip-contact-${x.toLowerCase()}`}>{x}</Chip>)}
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Anything else?</Label>
              <Textarea data-testid="concierge-notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={3} placeholder="Family hauler, good on gas, road-trip ready…" className="mt-1.5" />
            </div>
          </div>

          <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300"><ShieldCheck className="h-4 w-4 text-rose-600" /> What happens next</p>
            <ul className="mt-2 space-y-1 text-sm text-slate-600 dark:text-slate-300">
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" /> A licensed CA broker is assigned to your search</li>
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" /> We source matching cars and verify real stock</li>
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" /> You review options and track it all in My Garage</li>
            </ul>
          </div>

          <Button data-testid="concierge-submit-button" onClick={submit} disabled={busy} className="w-full rounded-md bg-slate-950 py-6 text-base font-semibold hover:bg-slate-800">
            {busy ? "Sending…" : "Send to a broker"}
          </Button>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
